import "./SiteNav.css";

import { ChevronDown, LogIn, Menu, Repeat, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import LanguageSelector from "@/3-features/i18n/LanguageSelector";
import { useT } from "@/3-features/i18n/useT";
import { getColdResourcesNav, getNavs, getSwitch, type NavEntry } from "@/3-features/navigation/nav";
import { PATHS } from "@/3-features/navigation/routes";
import { DecLogo } from "@/4-components/primitives/DecLogo";
import { Link, useAudience, useHasStoredAudience, useRoute } from "@/5-shared/routing";

const panelId = (label: string) => `nav-panel-${label.toLowerCase().replace(/\s+/g, "-")}`;

/** Center-nav variants. Both render into the DOM; a `data-known` probe in
 *  +Head reveals the right one before paint, so a returning visitor never sees
 *  the cold nav flash and crawlers keep the stripped Home + Resources markup. */
type NavVariant = "cold" | "full";

/** Renders responsive route navigation for the active audience. */
export function SiteNav() {
  const { t } = useT();
  const route = useRoute();
  const audience = useAudience(route);
  const known = useHasStoredAudience();
  const fullEntries = getNavs(t)[audience];
  const coldEntries = getColdResourcesNav(t);
  // Desktop renders both variants (CSS reveals one pre-paint). The mobile menu
  // is closed at load, so it can decide in JS without any visible flash.
  const mobileEntries = known ? fullEntries : coldEntries;
  const other = getSwitch(t)[audience];
  const [open, setOpen] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);

  const prevRoute = useRef(route);
  useEffect(() => {
    if (prevRoute.current !== route) {
      prevRoute.current = route;
      setOpen(null);
      setMobile(false);
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(null);
        setMobile(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [route]);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobile]);

  return (
    <header className="site-nav sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-zinc-200">
      <div className="site-nav__inner shell flex items-center justify-between gap-4">
        <Link to={PATHS.home} className="site-nav__brand flex shrink-0 items-center" aria-label="DEC Energy">
          <DecLogo className="h-10 w-auto" />
        </Link>

        <nav aria-label={t("nav.mainLabel")} className="site-nav__desktop hidden xl:flex min-w-0 items-center justify-center gap-1">
          <span data-nav="cold" className="contents">
            {coldEntries.map((e) => (
              <DesktopEntry key={e.label} variant="cold" entry={e} route={route} open={open === `cold:${e.label}`} onOpen={(value) => setOpen(value ? `cold:${e.label}` : null)} />
            ))}
          </span>
          <span data-nav="full" className="contents">
            {fullEntries.map((e) => (
              <DesktopEntry key={e.label} variant="full" entry={e} route={route} open={open === `full:${e.label}`} onOpen={(value) => setOpen(value ? `full:${e.label}` : null)} />
            ))}
          </span>
        </nav>

        <div className="site-nav__actions hidden xl:flex shrink-0 items-center justify-end gap-2">
          <Link
            data-nav="full"
            to={other.to}
            className="site-nav__switch flex items-center gap-1.5 rounded-full whitespace-nowrap transition-colors"
            title={t("nav.switchTitle")}
          >
            <Repeat className="w-3.5 h-3.5" /> {other.label}
          </Link>
          <LanguageSelector />
          <a
            href="https://app.dec-energy.ch"
            rel="noopener noreferrer"
            className="site-nav__login flex items-center gap-1.5 rounded-full whitespace-nowrap transition-colors"
          >
            <LogIn className="w-3.5 h-3.5" /> {t("nav.login")}
          </a>
          <Link
            to={PATHS.contact}
            className="site-nav__contact inline-flex items-center justify-center rounded-full bg-dec-green font-semibold text-white whitespace-nowrap transition-colors"
          >
            {t("nav.contact")}
          </Link>
        </div>

        <button
          className="xl:hidden p-2 text-zinc-700 justify-self-end"
          onClick={() => setMobile((value) => !value)}
          aria-label={mobile ? t("nav.closeMenu") : t("nav.openMenu")}
          aria-expanded={mobile}
          aria-controls="nav-mobile"
        >
          {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <nav
        id="nav-mobile"
        aria-label={t("nav.mainLabel")}
        aria-hidden={!mobile}
        data-open={mobile}
        inert={!mobile || undefined}
        className="xl:hidden border-t border-zinc-200 bg-white overflow-y-auto overflow-hidden max-h-0 opacity-0 transition-[max-height,opacity] duration-200 ease-out data-[open=true]:max-h-[calc(100dvh-var(--nav-height))] data-[open=true]:opacity-100 motion-reduce:transition-none"
      >
        <div className="shell space-y-5 py-4">
          {mobileEntries.map((e) => (
            <MobileEntry key={e.label} entry={e} route={route} onClose={() => setMobile(false)} />
          ))}
          {known && (
            <Link to={other.to} onClick={() => setMobile(false)} className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-500">
              <Repeat className="w-4 h-4" /> {other.label}
            </Link>
          )}
          <a href="https://app.dec-energy.ch" rel="noopener noreferrer" onClick={() => setMobile(false)} className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-500">
            <LogIn className="w-4 h-4" /> {t("nav.login")}
          </a>
          <div className="flex items-center justify-between gap-2 pt-2">
            <LanguageSelector onSelect={() => setMobile(false)} />
            <Link to={PATHS.contact} onClick={() => setMobile(false)} className="flex-1 justify-center btn btn-primary btn-sm px-4">
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}

function DesktopEntry({ entry, route, open, onOpen, variant }: { entry: NavEntry; route: string; open: boolean; onOpen: (open: boolean) => void; variant: NavVariant }) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  if (entry.kind === "link")
    return (
      <Link
        to={entry.to}
        aria-current={route === entry.to ? "page" : undefined}
        className="site-nav__link shrink-0 rounded-full whitespace-nowrap transition-colors"
      >
        {entry.label}
      </Link>
    );

  const active = route.startsWith(entry.base) || entry.items.some((i) => route === i.to);
  const id = panelId(`${variant}-${entry.label}`);
  return (
    <div
      className="site-nav__group relative"
      data-open={open}
      onMouseEnter={() => onOpen(true)}
      onMouseLeave={() => onOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => onOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onOpen(false);
          }
        }}
        onBlur={(e) => {
          if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) onOpen(false);
        }}
        data-active={active}
        className="site-nav__link site-nav__trigger flex shrink-0 items-center gap-1 rounded-full whitespace-nowrap transition-colors"
      >
        {entry.label}
        <ChevronDown className="site-nav__chevron h-[16px] w-[16px] transition-transform" />
      </button>
      <div id={id} role="presentation" className="site-nav__panel invisible pointer-events-none absolute left-1/2 top-full z-10 pt-2 opacity-0">
        <div className="site-nav__menu card rounded-2xl shadow-xl p-2">
          {entry.items.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              onClick={() => onOpen(false)}
              aria-current={route === i.to ? "page" : undefined}
              className="site-nav__dropdown-link block rounded-xl px-3 py-2.5 transition-colors [overflow-wrap:anywhere]"
            >
              <span className="site-nav__dropdown-title block font-semibold text-zinc-900">{i.label}</span>
              {i.desc && <span className="site-nav__dropdown-desc mt-0.5 block text-zinc-500">{i.desc}</span>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileEntry({ entry, route, onClose }: { entry: NavEntry; route: string; onClose: () => void }) {
  if (entry.kind === "link") {
    return (
      <Link to={entry.to} onClick={onClose} aria-current={route === entry.to ? "page" : undefined} className="site-nav__mobile-link site-nav__mobile-link--top block rounded-lg px-3 py-3 font-semibold">
        {entry.label}
      </Link>
    );
  }

  return (
    <div className="border-t border-zinc-100 pt-4">
      <p className="label text-xs font-medium uppercase tracking-wider text-zinc-400 mb-2 px-3">{entry.label}</p>
      <div className="space-y-1 pl-2">
        {entry.items.map((i) => (
          <Link key={i.to} to={i.to} onClick={onClose} aria-current={route === i.to ? "page" : undefined} className="site-nav__mobile-link block rounded-lg px-3 py-2.5 text-sm">
            {i.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
